import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Edit, Clock, Users, Calendar, FileText, Database, Settings } from 'lucide-react';
import { AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';

const permissionModules = [
  { nameKey: 'security.modules.attendance', icon: Clock, permissions: ['attendances:view','attendances:create','attendances:update','attendances:delete','attendances:export'] },
  { nameKey: 'security.modules.employees', icon: Users, permissions: ['employees:view','employees:create','employees:update','employees:delete','employees:import'] },
  { nameKey: 'security.modules.planning', icon: Calendar, permissions: ['planning:view','planning:create','planning:update','planning:delete'] },
  { nameKey: 'security.modules.reports', icon: FileText, permissions: ['reports:view','reports:create','reports:export'] },
  { nameKey: 'security.modules.payroll', icon: Database, permissions: ['payroll:view','payroll:create','payroll:update','payroll:export'] },
  { nameKey: 'security.modules.administration', icon: Settings, permissions: ['administration:view_users','administration:manage_roles','administration:manage_permissions'] },
];

// API returns permission objects {name, module, action, description}, not strings
const hasPermission = (rolePermissions, permission) =>
  rolePermissions.some(p => p.name === '*' || p.name === permission);

const SecurityRolesAccordionItem = ({ roles, setRoleDialog }) => {
  const { t } = useTranslation();

  return (
    <AccordionItem value="roles">
      <AccordionTrigger>
        <div className="flex items-center space-x-2">
          <Shield className="h-4 w-4" />
          <span>{t('security.roles.sectionTitle')}</span>
          <Badge variant="secondary">{roles.length}</Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="grid gap-4 md:grid-cols-2">
          {roles.map((role) => (
            <Card key={role.id}>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{role.name}</h3>
                      <p className="text-sm text-muted-foreground">{role.description}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline">{role.permissions.length} {t('security.roles.permissions')}</Badge>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">{t('security.roles.permissions')}</h4>
                    <div className="grid gap-2">
                      {permissionModules.map((module) => {
                        const hasAny = module.permissions.some((p) => hasPermission(role.permissions, p));
                        const hasAll = module.permissions.every((p) => hasPermission(role.permissions, p));
                        return (
                          <div key={module.nameKey} className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <module.icon className="h-4 w-4" />
                              <span className="text-sm">{t(module.nameKey)}</span>
                            </div>
                            {role.permissions.some(p => p.name === '*') ? (
                              <Badge variant="default" className="bg-green-600">{t('security.roles.full')}</Badge>
                            ) : hasAll ? (
                              <Badge variant="default">{t('security.roles.full')}</Badge>
                            ) : hasAny ? (
                              <Badge variant="outline">{t('security.roles.partial')}</Badge>
                            ) : (
                              <Badge variant="secondary">{t('security.roles.none')}</Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setRoleDialog({ open: true, role, mode: 'edit' })}>
                      <Edit className="mr-2 h-4 w-4" />{t('security.roles.edit')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default SecurityRolesAccordionItem;
